/**
 * Courbes de rendement et frontières de jour — SPEC-MOTEUR §6.2, §5.2, §9.1.
 *
 * « Les revenus se résolvent au fil des actions suivantes et au sommeil » (PLAN §4.1) :
 * une publication ne verse rien tout de suite, elle inscrit une courbe que chaque
 * frontière de jour franchie fait avancer d'une tranche.
 *
 * **Aucun gain ne se perd à l'arrondi** (§1.3) : vues, abonnés, Clicks et XP passent par
 * des accumulateurs fractionnaires portés par le `VideoRecord` (`frac_*`), et la dernière
 * tranche de la courbe initiale arrondit le report au lieu de le tronquer — le total versé
 * vaut donc exactement `round(bruts)` (§4.6).
 */
import { bandeAlignement } from "./actions.js";
import { H_JOUR } from "./clock.js";
import { retention } from "./progress.js";
// ─────────────────────────────────────────────────────────────────────────────
// §6.2 Distributions exactes (% de VuesPic par jour)
// ─────────────────────────────────────────────────────────────────────────────
/** Standard et Evergreen : J+1 → J+14 (Σ = 100). */
export const COURBE_STANDARD = [
    25, 18, 13, 10, 8, 6, 5, 4, 3, 2.5, 2, 1.5, 1.2, 0.8,
];
/** Trend publiée en phase émergente : 5 jours. */
export const COURBE_TREND_EMERGENTE = [45, 30, 15, 7, 3];
/** Trend publiée en pic : 4 jours. */
export const COURBE_TREND_PIC = [47, 30, 15, 8];
/** Trend publiée en saturée ou morte : 3 jours. */
export const COURBE_TREND_TARDIVE = [50, 33, 17];
/** Nulle : 3 jours (VuesPic est déjà minuscule), puis rendement nul. */
export const COURBE_NULLE = [60, 30, 10];
/** RPM (§5.2) — Clicks pour 1 000 vues. */
export const RPM_LONGUE = 4.5;
export const RPM_SHORT = 0.3;
/** Seuil de monétisation : dès 1 000 abonnés (PLAN §4.2). */
export const SEUIL_MONETISATION = 1000;
/**
 * La vidéo rapporte-t-elle des Clicks ?
 *
 * **Avenant A-1 « Le Grand Saut »** : toute la couche argent — entrées ET sorties —
 * s'active au palier 1 000, porté par `flags.grand_saut_done`. Le seuil d'abonnés et le
 * flag coïncident par construction (l'événement scripté se déclenche au franchissement) ;
 * on lit le **flag**, parce que c'est lui que l'avenant désigne comme commutateur.
 */
export function estMonetise(state) {
    return state.flags.grand_saut_done && state.subscribers >= SEUIL_MONETISATION;
}
/**
 * Distribution en pourcentages pour une classe donnée. `phasePublication` n'est lu que
 * pour les trends : c'est la phase de la tendance **à l'instant de la publication**.
 */
export function distribution(classe, phasePublication) {
    switch (classe) {
        case "nulle":
            return COURBE_NULLE;
        case "trend":
            if (phasePublication === "emergente")
                return COURBE_TREND_EMERGENTE;
            if (phasePublication === "pic")
                return COURBE_TREND_PIC;
            return COURBE_TREND_TARDIVE;
        case "standard":
        case "evergreen":
            return COURBE_STANDARD;
    }
}
/**
 * Construit `remaining_curve` : les **vues** à verser jour par jour (§1.3 — une case = un
 * jour simulé, `[0]` = prochain jour). La somme vaut `vuesPic` à la fraction près ; c'est
 * le versement qui garantit le total entier exact.
 */
export function construireCourbe(classe, vuesPic, phasePublication) {
    return distribution(classe, phasePublication).map((pct) => (vuesPic * pct) / 100);
}
/**
 * Verse **un** entier depuis un accumulateur fractionnaire. Sur la dernière tranche, le
 * report est arrondi (et non tronqué) : c'est la règle §4.6 qui fait que le total observé
 * vaut `round(bruts)`.
 */
function preleverEntier(accumulateur, derniere) {
    if (derniere)
        return { verse: Math.round(accumulateur), reste: 0 };
    const verse = Math.floor(accumulateur);
    return { verse, reste: accumulateur - verse };
}
export function verserTranche(video, state, events = [], mults = {}) {
    if (video.remaining_curve.length === 0)
        return null;
    if (video.visibility === "prive") {
        // La courbe continue de s'épuiser mais ne verse plus rien : le temps passe pour tout
        // le monde, y compris pour ce qu'on a caché.
        video.remaining_curve.shift();
        video.curve_cursor += 1;
        return null;
    }
    const brutVues = video.remaining_curve.shift();
    video.curve_cursor += 1;
    const derniere = video.remaining_curve.length === 0;
    // — Vues —
    const accVues = video.frac_views + brutVues;
    const vues = preleverEntier(accVues, derniere);
    video.frac_views = vues.reste;
    video.total_views += vues.verse;
    // — Abonnés : TC figé, poids volatil figé lui aussi (§4.6-4.7) —
    const volatile = video.format === "short" || bandeAlignement(video.alignment) === "chaos";
    const accSubs = video.frac_subs + brutVues * video.taux_conversion;
    const subs = preleverEntier(accSubs, derniere);
    video.frac_subs = subs.reste;
    video.total_subs += subs.verse;
    state.subscribers += subs.verse;
    if (volatile)
        state.subs_volatile += subs.verse;
    // — Clicks : dès 1 000 abonnés (§5.2) —
    let argentVerse = 0;
    if (estMonetise(state)) {
        // ⟦C4⟧ `rpm` × `revenus` — deux cibles de `multiplier` que personne ne lisait. Sans
        //   elles, `demonetisation_chaine` (×0,3 puis ×0,1) ne coûtait strictement rien.
        const rpm = (video.format === "short" ? RPM_SHORT : RPM_LONGUE) * (mults.rpm ?? 1);
        const accArgent = video.frac_money + (brutVues * rpm * (mults.revenus ?? 1)) / 1000;
        const argent = preleverEntier(accArgent, derniere);
        video.frac_money = argent.reste;
        state.money += argent.verse;
        argentVerse = argent.verse;
    }
    else {
        // Non monétisée : on n'accumule pas de dette de report — les vues de cette période
        // ne rapporteront jamais rien (le seuil est un fait, pas un différé).
        video.frac_money = 0;
    }
    // — XP = vues × rétention (§9.1) —
    const accXp = video.frac_xp + brutVues * retention(video.qualite_eff) * (mults.xp ?? 1);
    const xp = preleverEntier(accXp, derniere);
    video.frac_xp = xp.reste;
    state.xp += xp.verse;
    const payee = {
        video_id: video.id,
        vues: vues.verse,
        abonnes: subs.verse,
        argent: argentVerse,
        xp: xp.verse,
        derniere,
    };
    if (vues.verse > 0 || subs.verse > 0) {
        events.push({
            kind: "views_paid",
            data: {
                video_id: video.id,
                vues: vues.verse,
                abonnes: subs.verse,
                argent: argentVerse,
                xp: xp.verse,
                derniere,
            },
        });
    }
    return payee;
}
/**
 * §2.3 pas 5a — verse la tranche quotidienne de **chaque** vidéo dont `remaining_curve`
 * n'est pas épuisée. Ordre stable (par id croissant) : le déterminisme en dépend.
 *
 * Les charges fixes (−5 ₵/jour) sont débitées par la file de résolution elle-même
 * (`engine/actions.ts`, §2.3 pas 5b), pas ici.
 */
export function verserFrontiereDeJour(state, events = [], mults = {}) {
    const payees = [];
    for (const video of [...state.videos].sort((a, b) => a.id - b.id)) {
        const p = verserTranche(video, state, events, mults);
        if (p)
            payees.push(p);
    }
    return payees;
}
/** Statuts d'affichage de la Bibliothèque (§6.4) — dérivés, jamais stockés. */
export function statutAffichage(video, clockH) {
    if (video.class === "nulle" && video.visibility === "public")
        return "Nulle";
    const courbeEnCours = video.remaining_curve.length > 0;
    if (video.class === "trend")
        return courbeEnCours ? "En tendance" : "Morte";
    if (video.class === "evergreen" && video.evergreen) {
        const ageJ = (clockH - video.evergreen.anchor_h) / H_JOUR;
        const decay = Math.pow(0.5, ageJ / 90);
        return decay < 0.25 ? "En déclin" : "Evergreen";
    }
    if (video.class === "standard")
        return courbeEnCours ? "Active" : "Morte";
    return "Morte";
}
//# sourceMappingURL=curves.js.map